// use-licenses.ts
import { getLicenses } from 'API/license';
import { useEffect, useRef, useState } from 'react';
import useErrorStore from 'stores/error-store';
import { License } from 'types/licenses.types';
const useLicenses = () => {
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [licenses, setLicenses] = useState<License[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState<number>(1);
  const [searchParam, setSearchParam] = useState('');

  const itemsPerPage = 10;
  let typingTimer: ReturnType<typeof setTimeout>;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef<boolean>(false);

  const fetchLicenses = async (newPage: number = page, newSearch: string = searchParam, disableLoading: boolean = false) => {
    if (!disableLoading) {
      setLoading(true);
    }
    const res = await getLicenses(
      newPage - 1,
      itemsPerPage,
      'createdAt',
      'desc',
      newSearch
    );
    if (res && 'licenses' in res) {
      setLicenses(res.licenses);
      setTotal(res.total);
      setPage(newPage);
      stopPolling();
    } else {
      setLicenses([]);
      setTotal(0);
      setPage(1);
      if (res && 'error' in res) {
        handleOpenErrorDialog(res.error as any, '/license', 'get');
      }
    }
    if (!disableLoading) {
      setLoading(false);
    }
  };

  // Polling removed — no longer needed with simplified license model

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchLicense = async () => {
    fetchLicenses(1, searchParam);
  };

  const searchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleSearchLicense();
    }, 500);
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const handleClearSearchField = () => {
    setSearchParam('');
    fetchLicenses(1, '');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    fetchLicenses(newPage);
  };

  const resetLicenses = () => {
    setPage(1);
    setSearchParam('');
    stopPolling();
    fetchLicenses(1, '');
  };

  return {
    licenses,
    total,
    loading,
    itemsPerPage,
    page,
    searchParam,
    handleSearchParamChange,
    searchKeyUp,
    searchKeyDown,
    handleClearSearchField,
    handleChangePage,
    fetchLicenses,
    resetLicenses,
  };
};

export default useLicenses;
